import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AllPresetsModel } from '@common/components/create-user/models/users.models';
import { AccessService, getKeysFromBinary } from '@common/services/access.service';
import { AppLanguage, SUPPORTED_LANGUAGES } from '@common/utils/lang';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly httpClient = inject(HttpClient);
  private readonly accessService = inject(AccessService);

  selfChangePassword$ = new BehaviorSubject<boolean>(false);

  usersList(params: any = null) {
    let httpParams = new HttpParams();
    if (params?.fields?.length) {
      httpParams = httpParams.set('fields', params.fields.toString());
    }
    if (typeof params?.has_admin_access === 'boolean') {
      httpParams = httpParams.set('has_admin_access', params.has_admin_access.toString());
    }

    return this.httpClient.get<any[]>('users/', { params: httpParams }).pipe(
      map((list: any) => {
        return list.map((item: any) => ({
          ...item,
          currentUserAccess:
            typeof item.currentUserAccess === 'number'
              ? getKeysFromBinary(this.accessService.allAccessRights.users, item.currentUserAccess)
              : item.currentUserAccess,
        }));
      }),
    );
  }

  usersAccessList() {
    const httpParams = new HttpParams();
    // if (params?.fields?.length) {
    //   httpParams = httpParams.set('fields', params.fields.toString());
    // }

    return this.httpClient.get<any[]>('access/users/', { params: httpParams });
  }

  userById(userId: number) {
    return this.httpClient.get(`users/${userId}/`).pipe(
      map((item: any) => {
        return {
          ...item,
          currentUserAccess: getKeysFromBinary(
            this.accessService.allAccessRights.users,
            item?.currentUserAccess,
          ),
        };
      }),
    );
  }

  linkingObjectsByUserId(userId: number) {
    let httpParams = new HttpParams();

    httpParams = httpParams.set('user_id', userId.toString());

    return this.httpClient.get(`object_user_access/`, { params: httpParams });
  }

  getCurrentUser() {
    return this.httpClient.get('current_user/').pipe(
      map((user: any) => {
        return {
          ...user,
          language: SUPPORTED_LANGUAGES.includes(user.language)
            ? user.language
            : AppLanguage.English,
          commonAccesses:
            typeof user.commonAccesses === 'number'
              ? getKeysFromBinary(this.accessService.allAccessRights.common, user.commonAccesses)
              : user.commonAccesses,
        };
      }),
    );
  }

  updateUserProfile(body: any) {
    if (!this.accessService.checkUserCommonAccess(['access_edit_credentials'])) {
      delete body.username;
    }
    return this.httpClient.put('update_user_profile/', body);
  }

  changePassword(body: any) {
    return this.httpClient.put('change_password/', body);
  }

  getAccessPresets(): Observable<AllPresetsModel | null> {
    // TODO: uncomment when backend is ready
    // return this.httpClient.get('access_presets/');
    // TODO: update with any mock data
    return of(null);
  }

  updateAccessPresets(body: AllPresetsModel): Observable<AllPresetsModel> {
    // TODO: uncomment when backend is ready
    // return this.httpClient.get('access_presets/');
    // TODO: update with any mock data
    return of(body);
  }

  createUser(body: any): Observable<any> {
    return this.httpClient.post<any[]>('users/', body);
  }

  updateUser(body: any, id: string): Observable<any> {
    return this.httpClient.patch<any[]>(`users/${id}/`, body);
  }

  deleteUser(id: string): Observable<any> {
    return this.httpClient.delete<any[]>(`users/${id}/`);
  }

  // TODO: delete when backend is ready
  logmessages = [
    { time: '2018-01-01 00:00:00', message: 'message' },
    { time: '2018-01-01 00:00:00', message: 'message' },
  ];

  getUserLogs(body: any, id: string): Observable<any[]> {
    // TODO: uncomment when backend is ready
    // return this.httpClient.get('users/logs/' + id, body);
    return of(this.logmessages);
  }

  sessions = {
    access: ['aaaa', 'bbbb', 'cccc'],
    notifications: ['1111', '2222', '3333'],
  };

  getUserSessions(id: string): Observable<any> {
    // TODO: uncomment when backend is ready
    // return this.httpClient.get('users/sessions/' + id);
    return of({ ...this.sessions, id });
  }

  updateUserPassword(body: any) {
    return of(null);
  }
}
